import { useRef, useState } from 'react';
import { useMemo } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { MenuList } from '@mui/joy';
import { Box,Checkbox, FormControl, MenuItem, Select, TextField } from '@mui/material';

import CheckedIcon from '~/assets/icons/svgs/checkbox-ticked.svg?react';
import UnCheckedIcon from '~/assets/icons/svgs/checkbox-unticked.svg?react';
import { Text } from '~/components/ui/text';

import { useStyles } from './customSelect.styles';

export type CustomSelectProps = {
  title?: string;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  options: { label: string; value: string }[];
  multiple?: boolean;
  displayEmpty?: boolean;
  placeholder?: string;
  searchable?: boolean;
};

export function CustomSelect({
  title,
  value,
  onChange,
  options,
  multiple = false,
  displayEmpty = true,
  placeholder = 'Select',
  searchable = false,
}: CustomSelectProps) {
  const [search, setSearch] = useState('');
  const styles = useStyles();
  const renderValue = (selected: string[]) => {
    if (!multiple || selected.length === 0) return placeholder;
    const selectedValues = options.filter((item) => selected.includes(item.value)).map((i) => i.label);
    return selected.length > 1 ? `All Selected (${selected.length})` : selectedValues.join(', ');
  };

  const filteredOptions = useMemo(() => {
    return options.filter((option) => option.label?.toLowerCase().includes(search.toLowerCase()));
  }, [options, search]);

  return (
    <FormControl sx={styles.formControlSelect}>
      {title && (
        <Text sx={{ marginBottom: '5px' }} variant="inputVerySmall">
          {title}
        </Text>
      )}
      <Select
        multiple={multiple}
        value={value}
        displayEmpty={displayEmpty}
        onChange={(event) => {
          const val = event.target.value;
          if (val.includes('searchInput')) {
            return;
          }
          if (multiple) {
            onChange(typeof val === 'string' ? val.split(',') : val);
          }
          onChange(val);
        }}
        IconComponent={KeyboardArrowDownIcon}
        renderValue={multiple ? renderValue : undefined}
        MenuProps={{
          PaperProps: {
            sx: styles.paperSelect(multiple),
          },
        }}
      >
        {searchable && (
          <MenuItem value="searchInput" disableRipple>
            <TextField
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              size="small"
              placeholder="Search..."
              InputProps={{ sx: styles.searchInput }}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </MenuItem>
        )}
        {filteredOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {multiple && (
              <Checkbox
                icon={<UnCheckedIcon />}
                checkedIcon={<CheckedIcon />}
                checked={(value as string[]).includes(option.value)}
              />
            )}
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
